import { useCallback, useMemo } from 'react';

import { type ParamKeyValuePair, useSearchParams } from 'react-router-dom';

import { writeUpdates, writeValue } from './filterWriter';
import { type FilterStrategy, type FilterStrategyOptions } from './types';

/**
 * The filters a URL carries, as useNavigationFilter reads them — also for a
 * loader building the same query key before the page renders.
 */
export const readNavigationFilters = <T>(
  searchParams: URLSearchParams,
  ignoredKeys: string[] = ['show'],
  arrayKeys: string[] = [],
) => {
  const params = new URLSearchParams(searchParams);
  for (const key of ignoredKeys) {
    params.delete(key);
  }
  const filters: Record<string, unknown> = {};
  for (const key of new Set(params.keys())) {
    // arrayKeys are stored as a single comma-joined param (see setFilter/
    // setFilters/defaultFilters); split them back into an array on read.
    filters[key] = arrayKeys.includes(key)
      ? (params.get(key)?.split(',').filter(Boolean) ?? [])
      : params.get(key);
  }
  return filters as Partial<T>;
};

/**
 * A hook to manage navigation filters using the browser's URL search params.
 *
 * @param preserveFilters - Whether to preserve the existing filters when setting new ones.
 * @param ignoredKeys - Keys to ignore when setting filters.
 * @param defaultFilters - Default filters to use.
 * @param arrayKeys - Keys to treat as arrays.
 * @returns An object with the following properties:
 * - filters: The current filters.
 * - setFilter: A function to set a single filter.
 * - setFilters: A function to set multiple filters.
 * - clearFilters: A function to clear all filters.
 */
export function useNavigationFilter<T>({
  preserveFilters = true,
  ignoredKeys = ['show'],
  defaultFilters = {},
  arrayKeys = [],
}: FilterStrategyOptions<T> = {}): FilterStrategy<T> {
  const [searchParams, setSearchParams] = useSearchParams(
    Object.entries(defaultFilters).map(([key, value]) => [
      key,
      Array.isArray(value) ? value.join(',') : (value as string),
    ]) as ParamKeyValuePair[],
  );

  const filters = useMemo(
    () => readNavigationFilters<T>(searchParams, ignoredKeys, arrayKeys),
    [searchParams, ignoredKeys, arrayKeys],
  );

  const setFilter = useCallback(
    (key: keyof T, value: string | string[] | undefined) => {
      const newParams = new URLSearchParams(
        preserveFilters ? searchParams : {},
      );
      writeValue(newParams, key as string, value);
      // Only reset pagination on pages that actually paginate; otherwise a
      // spurious page=1 leaks into filters/query keys on non-paginated pages.
      if (newParams.has('page')) newParams.set('page', '1');
      setSearchParams(newParams, { replace: true });
    },
    [preserveFilters, searchParams, setSearchParams],
  );

  const setFilters = useCallback(
    (updates: Partial<Record<keyof T, string | string[] | undefined>>) => {
      const newParams = new URLSearchParams(
        preserveFilters ? searchParams : {},
      );
      writeUpdates(
        newParams,
        updates as Record<string, string | string[] | undefined>,
      );
      // Only reset pagination on pages that actually paginate (see setFilter).
      if (newParams.has('page')) newParams.set('page', '1');
      setSearchParams(newParams, { replace: true });
    },
    [preserveFilters, searchParams, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  return {
    setFilter,
    setFilters,
    clearFilters,
    filters,
  };
}
